import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { assistantService, challengeService } from "../../services/api";
import { timeLabel } from "./useWorkspace";
export function ReviewList({ items = [], limit = 6 }) {
  const [all, setAll] = useState(false);
  return (
    <div className="workspace-list">
      {items.slice(0, all ? undefined : limit).map((item) => (
        <article className="workspace-row" key={item.key}>
          <span className="workspace-tag">
            {item.kind === "draft" ? "Draft ready" : "Needs review"}
          </span>
          <h3>{item.title}</h3>
          <p>{item.reason}</p>
          <Link className="workspace-action" to={item.link}>
            Review details <span aria-hidden="true">↗</span>
          </Link>
        </article>
      ))}
      {items.length > limit && (
        <button className="workspace-text-link" onClick={() => setAll(!all)}>
          {all ? "Show less" : `Show all ${items.length} decisions`}
        </button>
      )}
      {!items.length && (
        <p className="workspace-empty">
          You’re all caught up. Decisions that need your review will appear
          here.
        </p>
      )}
    </div>
  );
}
export function GoalCard({ goal, timezone }) {
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const log = async () => {
    setBusy(true);
    try {
      await challengeService.adjust(goal.id, goal.today_ticked ? -1 : 1);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["workspace"] }),
        client.invalidateQueries({ queryKey: ["challenges"] }),
        client.invalidateQueries({ queryKey: ["challenge", goal.id] }),
      ]);
      toast.success(
        goal.today_ticked ? "Today’s check-in undone." : "Session logged.",
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Could not update your progress.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="goal-card">
      <div className="goal-card-top">
        <span className="workspace-eyebrow">{goal.kind}</span>
        {goal.today_ticked && (
          <span className="workspace-tag done">Done today</span>
        )}
      </div>
      <Link to={goal.link}>
        <h3>{goal.title}</h3>
      </Link>
      {goal.target > 0 && (
        <>
          <progress
            aria-label={`${goal.title} progress`}
            max={goal.target}
            value={goal.completed || 0}
          />
          <p className="workspace-caption">
            {goal.completed || 0} of {goal.target}{" "}
            {goal.kind === "plan" ? "steps" : "sessions"}
          </p>
        </>
      )}
      {goal.next_step && <p>{goal.next_step}</p>}
      {goal.next_at && (
        <p className="workspace-caption">
          Next: {timeLabel(goal.next_at, timezone)}
        </p>
      )}
      {goal.reminder_time && (
        <p className="workspace-caption">
          Check-in at {goal.reminder_time} · {timezone}
        </p>
      )}
      <div className="workspace-row-actions">
        <Link className="workspace-text-link" to={goal.link}>
          Open workspace
        </Link>
        {goal.kind === "challenge" && (
          <button className="workspace-action" disabled={busy} onClick={log}>
            {busy ? "Saving…" : goal.today_ticked ? "Undo today" : "Log today"}
          </button>
        )}
      </div>
    </article>
  );
}
export function AttentionList({ items = [], timezone }) {
  const [busy, setBusy] = useState(null);
  const client = useQueryClient();
  const snooze = async (item) => {
    setBusy(item.id);
    try {
      await assistantService.state(item.id, {
        action: "snooze",
        until: new Date(Date.now() + 86400000).toISOString(),
        expected_due_at: item.due_at,
      });
      await client.invalidateQueries({ queryKey: ["workspace"] });
      toast.success("Reminder snoozed for 24 hours.");
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Open the workspace to review this reminder.",
      );
    } finally {
      setBusy(null);
    }
  };
  const link = (item) =>
    item.details?.challenge_id
      ? `/challenges/${item.details.challenge_id}`
      : item.details?.event_id
        ? `/meetings/${item.details.event_id}`
        : item.details?.source_id
          ? `/trackers/${item.details.source_id}`
          : "/manage?view=attention";
  return (
    <div className="workspace-list">
      {items.slice(0, 8).map((item) => (
        <article className="workspace-row" key={item.id}>
          <span
            className={`workspace-tag ${item.priority === "critical" ? "urgent" : ""}`}
          >
            {item.priority === "critical" ? "Urgent" : item.domain}
          </span>
          <h3>{item.title}</h3>
          <p>{item.reason || item.description}</p>
          {item.due_at && (
            <p className="workspace-caption">
              Due {timeLabel(item.due_at, timezone)}
            </p>
          )}
          <div className="workspace-row-actions">
            <Link className="workspace-action" to={link(item)}>
              Review next step
            </Link>
            <button
              className="workspace-text-link"
              disabled={busy === item.id}
              onClick={() => snooze(item)}
            >
              Snooze 24h
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
export function RuntimeNote({ data }) {
  if (!data) return null;
  const text = !data.monitoring_enabled
    ? "Background monitoring is off."
    : data.workers?.host_may_sleep
      ? "Scheduled checks can be delayed while the testing server sleeps."
      : !data.workers?.operational
        ? "Some background checks need attention."
        : "Background checks are running.";
  return (
    <div className="workspace-runtime">
      <span>{text}</span>
      <Link to="/manage?view=settings">Review settings</Link>
    </div>
  );
}
